/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/** True when a dynamic import failed because a redeployed chunk file is missing.
 *  Covers the JS module-fetch failures and Vite's CSS preload failure, both of which
 *  surface after a deploy swaps the hashed asset filenames out from under an open tab. */
export function isChunkLoadError(err: unknown): boolean {
  const text = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
  return /failed to fetch dynamically imported module|error loading dynamically imported module|chunkloaderror|loading chunk \d+ failed|unable to preload css/i.test(
    text,
  );
}

const RELOAD_KEY = 'chunkReloadAt';
const RELOAD_GATE_MS = 10_000;

/** Reload the page once to recover from a stale chunk after redeploy, throttled via a timestamp in
 *  sessionStorage so a persistently broken deploy can't trap the tab in a reload loop. The gate must
 *  survive the reload to be safe, so if sessionStorage is unavailable (privacy mode, sandboxed
 *  iframes) we skip the auto-reload entirely rather than risk looping -- the router error boundary
 *  still surfaces a manual refresh button in that case. Returns true when it reloaded. */
export function reloadForStaleChunk(): boolean {
  let last: number;
  try {
    last = Number(sessionStorage.getItem(RELOAD_KEY)) || 0;
  } catch {
    return false; // can't read the gate -- don't risk a reload loop we can't throttle
  }
  if (Date.now() - last <= RELOAD_GATE_MS) return false;
  try {
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    return false; // can't persist the gate across the reload -- skip it to avoid looping
  }
  window.location.reload();
  return true;
}

/** Reload once on stale chunk load errors after redeploy; rethrow other failures. */
export function withReload<T>(factory: () => Promise<T>): () => Promise<T> {
  return () =>
    factory().catch((err) => {
      if (!isChunkLoadError(err)) throw err;
      if (reloadForStaleChunk()) return new Promise<T>(() => {}); // hang until the reload takes over
      throw err;
    });
}

let preloadReloadInstalled = false;

/** App-wide safety net: Vite fires `vite:preloadError` for dynamic-import preload failures,
 *  including ones outside the routed lazy() boundaries (e.g. the 3D viewer). Recover from the
 *  stale-chunk ones the same way, but only those - transient network blips shouldn't yank the tab
 *  out from under the user. Idempotent so repeated calls never stack listeners. */
export function installStaleChunkReload(): void {
  if (preloadReloadInstalled) return;
  preloadReloadInstalled = true;
  window.addEventListener('vite:preloadError', (event) => {
    if (isChunkLoadError((event as { payload?: unknown }).payload)) reloadForStaleChunk();
  });
}
